import React, { useMemo, useState } from "react";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  Customized,
} from "recharts";
import { Activity, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

// Bin scheme 1: counts are positional over these edges; the last bin is
// open-ended. Midpoints are used to apportion a window's Σg across bins when
// only some bins are selected (the firmware stores one Σg per window, not one
// per bin). With every bin selected the true Σg is used unchanged.
export const LOAD_BIN_EDGES = [3, 4, 5, 6, 8, 10];
const BIN_LABELS = ["3–4g", "4–5g", "5–6g", "6–8g", "8–10g", "10g+"];
const BIN_MID = [3.5, 4.5, 5.5, 7, 9, 12];
const BIN_TOP = [4, 5, 6, 8, 10, Infinity];

/**
 * Load-over-time chart for one or more series of 30 s load windows — the
 * same treatment as the session Load tab, but fed from local data (e.g. the
 * LOAD_BINS records in exported files) rather than the API:
 *
 *   - re-bucket client-side (30 s → 15 min), per window or cumulative
 *   - intensity zones as faint bands, thresholds scaled to the bucket
 *   - series toggles in the legend; hover a zone to light up that band and
 *     the matching stretches of every curve; "Paint by zone" colours the
 *     curves by the band they sit in instead of by unit
 *   - tooltip with every visible series and its zone for the window
 *   - a per-series minutes strip (played / high / medium / peak)
 *
 * series: [{ id, name, color, windows: [{ offsetMs, sumG, counts?, peakG? }] }]
 * offsetMs is elapsed since that series' own session start (native 30 s
 * windows). Fills the height it is given; parent should be a flex column.
 */

// Display names live under analysis.loadChart.zone.<key>.
export const LOAD_ZONES = [
  { key: "light", max: 45, color: "#22C55E" },
  { key: "medium", max: 100, color: "#F59E0B" },
  { key: "intense", max: Infinity, color: "#EF4444" },
];
const ZONE_FILL_OPACITY = 0.055;
const MUTED = "#D1D5DB";

const BUCKETS = [
  { seconds: 30, label: "30s" },
  { seconds: 60, label: "1m" },
  { seconds: 120, label: "2m" },
  { seconds: 300, label: "5m" },
  { seconds: 600, label: "10m" },
  { seconds: 900, label: "15m" },
];

const formatElapsed = (seconds, bucketSeconds) => {
  const sign = seconds < 0 ? "−" : "";
  const abs = Math.abs(Math.round(seconds));
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  if (h > 0) return `${sign}${h}h${String(m).padStart(2, "0")}`;
  if (bucketSeconds < 60 && s !== 0)
    return `${sign}${m}:${String(s).padStart(2, "0")}`;
  return `${sign}${m}m`;
};

// "+1:30" / "−0:30" for a per-unit time shift.
const formatShift = (ms) => {
  const sign = ms < 0 ? "−" : "+";
  const abs = Math.abs(ms) / 1000;
  const m = Math.floor(abs / 60);
  const s = Math.round(abs % 60);
  return `${sign}${m}:${String(s).padStart(2, "0")}`;
};

const segment = (active) =>
  `rounded-md px-2 py-1 text-[11px] font-medium transition duration-150 ${
    active
      ? "bg-white text-[#111827] shadow-sm"
      : "text-[#6B7280] hover:text-[#374151]"
  }`;

const LoadSeriesChart = ({
  series = [],
  nativeSeconds = 30,
  defaultBucket = 60,
  title,
  subtitle,
}) => {
  const { t } = useTranslation();
  const zoneLabel = (z) => t(`analysis.loadChart.zone.${z.key}`);
  const [bucket, setBucket] = useState(defaultBucket);
  const [view, setView] = useState("window"); // "window" | "cumulative"
  const [paint, setPaint] = useState("units"); // "units" | "zones"
  const [hidden, setHidden] = useState(() => new Set());
  const [hoverZone, setHoverZone] = useState(null);
  // Per-unit time shift in ms (id → ms), nudged one native window at a time
  // to line files up whose session clocks didn't start together.
  const [shifts, setShifts] = useState({});
  // Which bins feed the curve. All on by default; untick to look at the load
  // from a subset (e.g. drop 3–4g noise, or 10g+ only).
  const [bins, setBins] = useState(() => new Set(BIN_LABELS.map((_, i) => i)));
  const allBins = bins.size === BIN_LABELS.length;
  const toggleBin = (i) =>
    setBins((prev) => {
      const n = new Set(prev);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });
  // Load a window contributes given the selected bins: the true Σg when all
  // bins are on, else Σg apportioned by count × bin midpoint.
  const loadOf = (w) => {
    const total = w.sumG || 0;
    if (allBins || !w.counts) return total;
    let all = 0;
    let sel = 0;
    w.counts.forEach((c, i) => {
      const wgt = (c || 0) * (BIN_MID[i] ?? 0);
      all += wgt;
      if (bins.has(i)) sel += wgt;
    });
    return all > 0 ? (total * sel) / all : 0;
  };
  const eventsOf = (w) =>
    (w.counts || []).reduce((a, c, i) => a + (bins.has(i) ? c || 0 : 0), 0);
  // A window's peak can't be attributed to a bin, so with bins excluded it
  // is capped at the top of the highest selected bin.
  const peakOf = (w) => {
    const p = w.peakG || 0;
    if (allBins) return p;
    const top = Math.max(-Infinity, ...[...bins].map((i) => BIN_TOP[i]));
    return Math.min(p, top);
  };
  const shiftStepMs = nativeSeconds * 1000;
  const nudge = (id, dir) =>
    setShifts((prev) => ({
      ...prev,
      [id]: (prev[id] || 0) + dir * shiftStepMs,
    }));
  const resetShift = (id) =>
    setShifts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  const isCumulative = view === "cumulative";

  const zoneScale = bucket / nativeSeconds;
  const zones = LOAD_ZONES.map((z) => ({ ...z, max: z.max * zoneScale }));
  const zoneFor = (v) =>
    zones.find((z) => v < z.max) || zones[zones.length - 1];

  // Re-bucket every series onto one elapsed-time grid. Beyond a series' last
  // window its value is null so a shorter file ends rather than flatlining.
  const { rows, stats } = useMemo(() => {
    const bucketMs = bucket * 1000;
    const perSeries = series.map((s) => {
      const buckets = new Map();
      const shift = shifts[s.id] || 0;
      let firstIdx = Infinity;
      let lastIdx = -Infinity;
      for (const w of s.windows || []) {
        const idx = Math.floor(((w.offsetMs || 0) + shift) / bucketMs);
        const b = buckets.get(idx) || { sumG: 0, peak: 0, count: 0 };
        b.sumG += loadOf(w);
        b.peak = Math.max(b.peak, peakOf(w));
        b.count += eventsOf(w);
        buckets.set(idx, b);
        if (idx < firstIdx) firstIdx = idx;
        if (idx > lastIdx) lastIdx = idx;
      }
      return { ...s, buckets, firstIdx, lastIdx };
    });
    const withData = perSeries.filter((s) => s.lastIdx >= s.firstIdx);
    // Grid spans every series (shifts can push a start before 0:00).
    const minIdx = Math.min(0, ...withData.map((s) => s.firstIdx));
    const maxIdx = Math.max(-1, ...withData.map((s) => s.lastIdx));
    const out = [];
    const cum = Object.fromEntries(perSeries.map((s) => [s.id, 0]));
    for (let i = minIdx; i <= maxIdx; i++) {
      const elapsed = i * bucket;
      const row = { i, elapsed, label: formatElapsed(elapsed, bucket) };
      for (const s of perSeries) {
        if (i > s.lastIdx || i < s.firstIdx) {
          row[s.id] = null;
          row[`${s.id}__cum`] = null;
          continue;
        }
        const b = s.buckets.get(i);
        const v = b ? b.sumG : 0;
        cum[s.id] += v;
        row[s.id] = v;
        row[`${s.id}__cum`] = cum[s.id];
        row[`${s.id}__peak`] = b ? b.peak : 0;
        row[`${s.id}__events`] = b ? b.count : 0;
      }
      out.push(row);
    }
    // Minutes per series from the native windows (bucket-independent).
    const nativeMin = nativeSeconds / 60;
    const nativeZones = LOAD_ZONES;
    const st = series.map((s) => {
      const acc = { played: 0, high: 0, medium: 0, peak: 0, total: 0 };
      for (const w of s.windows || []) {
        const g = loadOf(w);
        acc.total += g;
        acc.peak = Math.max(acc.peak, peakOf(w));
        if (g <= 0) continue;
        acc.played += nativeMin;
        const z = nativeZones.find((zz) => g < zz.max) || nativeZones[2];
        if (z.key === "intense") acc.high += nativeMin;
        else if (z.key === "medium") acc.medium += nativeMin;
      }
      return { id: s.id, ...acc };
    });
    return { rows: out, stats: st };
  }, [series, bucket, nativeSeconds, shifts, bins]);

  const visible = series.filter((s) => !hidden.has(s.id));
  const toggle = (id) =>
    setHidden((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const strokeFor = (s) => {
    if (isCumulative) return s.color;
    if (paint === "zones") return "url(#lsc-zone)";
    if (hoverZone) return `url(#lsc-unit-${s.id})`;
    return s.color;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    const end = formatElapsed(row.elapsed + bucket, bucket);
    return (
      <div className="min-w-[200px] rounded-lg border border-[#F3F4F6] bg-white px-3 py-2.5 shadow-lg">
        <p className="mb-1.5 text-[13px] font-semibold text-[#111827]">
          {row.label} – {end}
          {isCumulative && (
            <span className="ml-1 text-[10px] font-normal text-[#9CA3AF]">
              {t("analysis.loadChart.soFar")}
            </span>
          )}
        </p>
        <div className="space-y-1.5 border-t border-[#F3F4F6] pt-1.5">
          {visible.map((s) => {
            const v = isCumulative ? row[`${s.id}__cum`] : row[s.id];
            if (v == null) return null;
            const z = isCumulative ? null : zoneFor(v);
            return (
              <div
                key={s.id}
                className="flex items-center justify-between gap-3"
              >
                <span className="flex items-center gap-1.5 text-[11px] text-[#6B7280]">
                  <span
                    className="h-2 w-2 rounded"
                    style={{ backgroundColor: s.color }}
                  />
                  {s.name}
                  {z && (
                    <span
                      className="rounded px-1 py-px text-[9px] font-medium"
                      style={{
                        color: z.color,
                        backgroundColor: `${z.color}14`,
                      }}
                    >
                      {zoneLabel(z)}
                    </span>
                  )}
                </span>
                <span className="text-xs font-semibold tabular-nums text-[#111827]">
                  {Math.round(v).toLocaleString()}
                  <span className="ml-0.5 text-[10px] font-normal text-[#6B7280]">
                    g
                  </span>
                  {!isCumulative && (
                    <span className="ml-1.5 text-[10px] font-normal text-[#9CA3AF]">
                      {row[`${s.id}__peak`] > 0
                        ? t("analysis.loadChart.tooltipEventsPeak", {
                            events: row[`${s.id}__events`] || 0,
                            peak: row[`${s.id}__peak`].toFixed(1),
                          })
                        : t("analysis.loadChart.tooltipEvents", {
                            events: row[`${s.id}__events`] || 0,
                          })}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (series.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center rounded-xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] px-4 py-10 text-center">
        <div>
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[#FEF3E7]">
            <Activity size={22} className="text-[#E07A3A]" />
          </div>
          <p className="text-sm font-semibold text-[#111827]">
            {t("analysis.loadChart.emptyTitle")}
          </p>
          <p className="mx-auto mt-1 max-w-xs text-xs leading-relaxed text-[#6B7280]">
            {t("analysis.loadChart.emptyBody")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border border-[#F3F4F6] bg-white">
      {/* Header: title + controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#F3F4F6] px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-[#111827]">
            {title ?? t("analysis.loadChart.title")}
          </h3>
          <p className="text-xs text-[#6B7280]">
            {subtitle ?? t("analysis.loadChart.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-0.5 rounded-lg bg-[#F3F4F6] p-0.5">
            {[
              { key: "units", label: t("analysis.loadChart.byUnit") },
              { key: "zones", label: t("analysis.loadChart.byZone") },
            ].map((p) => (
              <button
                key={p.key}
                onClick={() => setPaint(p.key)}
                disabled={isCumulative}
                title={
                  isCumulative
                    ? t("analysis.loadChart.zonesPerWindowOnly")
                    : undefined
                }
                className={`${segment(paint === p.key)} disabled:opacity-40`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-0.5 rounded-lg bg-[#F3F4F6] p-0.5">
            {[
              { key: "window", label: t("analysis.loadChart.perWindow") },
              { key: "cumulative", label: t("analysis.loadChart.cumulative") },
            ].map((v) => (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                className={segment(view === v.key)}
              >
                {v.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-0.5 rounded-lg bg-[#F3F4F6] p-0.5">
            {BUCKETS.map((b) => (
              <button
                key={b.seconds}
                onClick={() => setBucket(b.seconds)}
                className={segment(bucket === b.seconds)}
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Per-series minutes strip */}
      <div className="grid gap-2 border-b border-[#F3F4F6] px-4 py-3 sm:grid-cols-2 xl:grid-cols-3">
        {series.map((s) => {
          const st = stats.find((x) => x.id === s.id);
          const off = hidden.has(s.id);
          const shift = shifts[s.id] || 0;
          return (
            <div
              key={s.id}
              className="flex items-center gap-3 rounded-lg border border-[#F3F4F6] bg-[#FAFAFA] px-3 py-2"
              style={{ opacity: off ? 0.45 : 1 }}
            >
              {/* Name toggles the series */}
              <button
                onClick={() => toggle(s.id)}
                title={
                  off
                    ? t("analysis.loadChart.showSeries")
                    : t("analysis.loadChart.hideSeries")
                }
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
              >
                <span
                  className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: off ? MUTED : s.color }}
                />
                <span
                  className={`min-w-0 flex-1 truncate text-xs font-semibold text-[#111827] ${
                    off ? "line-through" : ""
                  }`}
                >
                  {s.name}
                </span>
              </button>
              {/* Shift this unit's timeline one native window at a time */}
              <span className="flex flex-shrink-0 items-center gap-0.5 rounded-md border border-[#E5E7EB] bg-white">
                <button
                  onClick={() => nudge(s.id, -1)}
                  title={t("analysis.loadChart.shiftEarlierBy", {
                    name: s.name,
                    seconds: nativeSeconds,
                  })}
                  aria-label={t("analysis.loadChart.shiftEarlier", {
                    name: s.name,
                  })}
                  className="flex h-6 w-6 items-center justify-center rounded-l-md text-[#6B7280] transition duration-150 hover:bg-[#F3F4F6] hover:text-[#111827]"
                >
                  <ChevronLeft size={12} />
                </button>
                <button
                  onClick={() => resetShift(s.id)}
                  disabled={shift === 0}
                  title={
                    shift === 0
                      ? t("analysis.loadChart.noShift")
                      : t("analysis.loadChart.shiftedReset", {
                          shift: formatShift(shift),
                        })
                  }
                  className={`min-w-[44px] px-1 text-center text-[11px] tabular-nums transition duration-150 ${
                    shift === 0
                      ? "text-[#C4C8CE]"
                      : "font-semibold text-[#E07A3A] hover:text-[#B45309]"
                  }`}
                >
                  {shift === 0 ? "0:00" : formatShift(shift)}
                </button>
                <button
                  onClick={() => nudge(s.id, +1)}
                  title={t("analysis.loadChart.shiftLaterBy", {
                    name: s.name,
                    seconds: nativeSeconds,
                  })}
                  aria-label={t("analysis.loadChart.shiftLater", {
                    name: s.name,
                  })}
                  className="flex h-6 w-6 items-center justify-center rounded-r-md text-[#6B7280] transition duration-150 hover:bg-[#F3F4F6] hover:text-[#111827]"
                >
                  <ChevronRight size={12} />
                </button>
              </span>
              <span className="flex items-center gap-3 text-[11px] tabular-nums text-[#6B7280]">
                <span title={t("analysis.loadChart.minutesAnyLoad")}>
                  <Trans
                    i18nKey="analysis.loadChart.stripMinutes"
                    values={{ n: Math.round(st.played) }}
                    components={{
                      value: <span className="font-semibold text-[#111827]" />,
                    }}
                  />
                </span>
                <span title={t("analysis.loadChart.minutesIntense")}>
                  <Trans
                    i18nKey="analysis.loadChart.stripHigh"
                    values={{ n: Math.round(st.high) }}
                    components={{
                      value: (
                        <span
                          className="font-semibold"
                          style={{ color: LOAD_ZONES[2].color }}
                        />
                      ),
                    }}
                  />
                </span>
                <span title={t("analysis.loadChart.minutesMedium")}>
                  <Trans
                    i18nKey="analysis.loadChart.stripMedium"
                    values={{ n: Math.round(st.medium) }}
                    components={{
                      value: (
                        <span
                          className="font-semibold"
                          style={{ color: LOAD_ZONES[1].color }}
                        />
                      ),
                    }}
                  />
                </span>
                <span
                  title={t("analysis.loadChart.peakSingleEvent")}
                  className="text-[#9CA3AF]"
                >
                  {t("analysis.loadChart.stripPeak", {
                    peak: st.peak.toFixed(1),
                  })}
                </span>
              </span>
            </div>
          );
        })}
      </div>

      {/* Bin filter — which g bands feed the curve */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-[#F3F4F6] px-4 py-2">
        <span className="mr-1 text-[11px] text-[#9CA3AF]">
          {t("analysis.loadChart.bins")}
        </span>
        {BIN_LABELS.map((label, i) => {
          const on = bins.has(i);
          return (
            <button
              key={label}
              onClick={() => toggleBin(i)}
              role="checkbox"
              aria-checked={on}
              className={`flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium transition duration-150 ${
                on
                  ? "border-[#E07A3A] bg-[#FFF3ED] text-[#B45309]"
                  : "border-[#E5E7EB] bg-white text-[#9CA3AF] hover:border-[#D1D5DB] hover:text-[#6B7280]"
              }`}
            >
              <span
                className={`flex h-3 w-3 items-center justify-center rounded-sm border ${
                  on
                    ? "border-[#E07A3A] bg-[#E07A3A] text-white"
                    : "border-[#D1D5DB] bg-white"
                }`}
              >
                {on && <Check size={9} strokeWidth={3} />}
              </span>
              {label}
            </button>
          );
        })}
        {!allBins && (
          <button
            onClick={() => setBins(new Set(BIN_LABELS.map((_, i) => i)))}
            className="ml-1 text-[11px] font-medium text-[#6B7280] underline-offset-2 hover:text-[#374151] hover:underline"
          >
            {t("analysis.loadChart.allBins")}
          </button>
        )}
        {!allBins && (
          <span className="ml-auto text-[11px] text-[#9CA3AF]">
            {t("analysis.loadChart.apportioned")}
          </span>
        )}
      </div>

      {/* Zone legend — hover to highlight */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pb-1 pt-3">
        <span className="text-[11px] text-[#9CA3AF]">
          {t(
            isCumulative
              ? "analysis.loadChart.legendSummary"
              : "analysis.loadChart.legendSummaryHover",
            {
              windows: rows.length,
              count: series.length,
              seconds: nativeSeconds,
            }
          )}
        </span>
        {!isCumulative && (
          <div className="flex items-center gap-1">
            {LOAD_ZONES.map((z) => {
              const active = hoverZone === z.key;
              const faded = hoverZone != null && !active;
              return (
                <span
                  key={z.key}
                  onMouseEnter={() => setHoverZone(z.key)}
                  onMouseLeave={() => setHoverZone(null)}
                  className="flex cursor-default items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] transition duration-150"
                  style={{
                    backgroundColor: active ? `${z.color}14` : "transparent",
                    opacity: faded ? 0.4 : 1,
                    color: active ? z.color : "#6B7280",
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: z.color }}
                  />
                  {zoneLabel(z)}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Chart fills the rest */}
      <div className="min-h-[220px] flex-1 px-2 pb-3">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            margin={{ top: 8, right: 8, left: -16, bottom: 4 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#F3F4F6"
            />
            {!isCumulative &&
              zones.map((z, i) => (
                <ReferenceArea
                  key={z.key}
                  y1={i === 0 ? 0 : zones[i - 1].max}
                  y2={isFinite(z.max) ? z.max : undefined}
                  fill={z.color}
                  fillOpacity={
                    hoverZone == null
                      ? ZONE_FILL_OPACITY
                      : hoverZone === z.key
                      ? 0.16
                      : 0.02
                  }
                  stroke="none"
                  ifOverflow="visible"
                  label={{
                    value: zoneLabel(z),
                    position: "insideTopRight",
                    fontSize: 10,
                    fill: z.color,
                    fillOpacity:
                      hoverZone == null || hoverZone === z.key ? 0.9 : 0.3,
                    dx: -4,
                    dy: 2,
                  }}
                />
              ))}
            {/* Gradients in plot pixels with hard stops at the zone edges:
                one zone-coloured stroke, and one per unit that keeps the unit
                colour inside the hovered zone and greys the rest. */}
            {!isCumulative && (
              <Customized
                component={(chart) => {
                  const axis = Object.values(chart.yAxisMap || {})[0];
                  const off = chart.offset;
                  if (!axis?.scale || !off) return null;
                  const top = off.top;
                  const bottom = off.top + off.height;
                  const span = bottom - top || 1;
                  const at = (v) =>
                    Math.min(1, Math.max(0, (axis.scale(v) - top) / span));
                  const [light, medium, intense] = zones;
                  const stops = [
                    [0, intense],
                    [at(medium.max), intense],
                    [at(medium.max), medium],
                    [at(light.max), medium],
                    [at(light.max), light],
                    [1, light],
                  ];
                  const zoneColour = (z) =>
                    hoverZone == null || hoverZone === z.key ? z.color : MUTED;
                  const grad = (id, colourOf) => (
                    <linearGradient
                      key={id}
                      id={id}
                      gradientUnits="userSpaceOnUse"
                      x1={0}
                      y1={top}
                      x2={0}
                      y2={bottom}
                    >
                      {stops.map(([o, z], i) => (
                        <stop key={i} offset={o} stopColor={colourOf(z)} />
                      ))}
                    </linearGradient>
                  );
                  return (
                    <defs>
                      {grad("lsc-zone", zoneColour)}
                      {series.map((s) =>
                        grad(`lsc-unit-${s.id}`, (z) =>
                          hoverZone == null || hoverZone === z.key
                            ? s.color
                            : MUTED
                        )
                      )}
                    </defs>
                  );
                }}
              />
            )}
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#9CA3AF" }}
              dy={8}
              minTickGap={28}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#9CA3AF" }}
              dx={-5}
              domain={
                isCumulative
                  ? [0, "auto"]
                  : [
                      0,
                      (dataMax) =>
                        Math.ceil(Math.max(dataMax, zones[1].max * 1.3) / 10) *
                        10,
                    ]
              }
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: "#E5E7EB", strokeWidth: 1 }}
            />
            {visible.length === 1 ? (
              <Area
                type="monotone"
                dataKey={isCumulative ? `${visible[0].id}__cum` : visible[0].id}
                name={visible[0].name}
                stroke={strokeFor(visible[0])}
                strokeWidth={2}
                fill={strokeFor(visible[0])}
                fillOpacity={paint === "zones" && !isCumulative ? 0.14 : 0.1}
                isAnimationActive={false}
                dot={false}
                activeDot={{ r: 3, strokeWidth: 0 }}
                connectNulls={false}
              />
            ) : (
              visible.map((s) => (
                <Line
                  key={s.id}
                  type="monotone"
                  dataKey={isCumulative ? `${s.id}__cum` : s.id}
                  name={s.name}
                  stroke={strokeFor(s)}
                  strokeWidth={2}
                  isAnimationActive={false}
                  dot={false}
                  activeDot={{ r: 3, strokeWidth: 0 }}
                  connectNulls={false}
                />
              ))
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LoadSeriesChart;
